/**
 * Capacitor native background video upload (@capgo/capacitor-uploader).
 *
 * Reliable large-video uploads need the client→S3 byte transfer to survive
 * the Capacitor WebView being suspended (app backgrounded/locked) and
 * network changes (Wi-Fi <-> cellular) mid-transfer. Uploads run as
 * JavaScript in the WebView, so iOS pauses them on background — this plugin
 * hands the PUT to iOS's native background `URLSession` (Android: WorkManager
 * foreground service), which keeps going while the app is suspended.
 *
 * Mirrors the `capacitor/push_registration.js` bridge pattern: lazy memoized
 * dynamic `import()` (clear-on-failure) so the plugin is never downloaded or
 * parsed outside the Capacitor shell, guards on
 * `isCapacitorEnvironment() && isCapacitorPluginAvailable("Uploader")`
 * ("Uploader" is the plugin's registerPlugin() name — see its
 * dist/esm/index.js), a single `'events'` listener registered once that fans
 * out to per-upload promise resolvers keyed by the `id` `startUpload()`
 * returns, and `reportError -> Honeybadger.notify(..,
 * {context:{component:"capacitor_background_upload"}})`.
 *
 * This is the ONLY plugin evaluated that meets every hard requirement: raw
 * binary PUT to a presigned S3 URL, custom headers passed verbatim, iOS
 * background URLSession + Android WorkManager, and an events channel we can
 * correlate by task id. If it is ever unavailable (old app build, plugin
 * failed to load, flag off), `isBackgroundUploadAvailable()` returns false
 * and callers fall back to the existing JS upload paths — see
 * `components/shared/videos/upload_video.js`.
 */

import Honeybadger from "@honeybadger-io/js";
import {
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
} from "./detection";

// The plugin's registerPlugin() name (dist/esm/index.js:
// `registerPlugin('Uploader', ...)`), NOT the npm package name. The JS gate
// hard-depends on this string matching exactly what the native shell
// registers, or isCapacitorPluginAvailable silently disables the feature.
const UPLOADER_PLUGIN_NAME = "Uploader";

// iOS background URLSession uploads must read from a `file://` path — it
// cannot upload an in-memory Blob. So the native branch first materializes
// the picked File to disk via @capacitor/filesystem, which means reading it
// (in bounded chunks — see materializeNativeTempFile) and base64-encoding it
// in JS (writeFile/appendFile only accept base64/UTF8 strings from the JS
// side, not raw binary). RISK (launch blocker per the background-upload
// plan): keep this ceiling conservative until proven on real devices — files
// above it fall back to the existing chunked JS upload path instead of
// attempting native.
export const NATIVE_UPLOAD_MAX_BYTES = 150 * 1024 * 1024; // 150MB

// How long to wait for a terminal ('completed'/'failed') event for a given
// upload id before giving up. Generous on purpose — the entire point of this
// module is surviving long backgrounding, so this is NOT a transfer-speed
// timeout, only a dead-man's switch for a genuinely lost/dropped native event
// (e.g. the app was force-quit and relaunched, losing this JS module's
// in-memory pendingUploads entry, or the native side failed to emit a
// terminal event at all). Without this, a dropped event hangs the caller's
// promise forever with no error ever surfaced.
const NATIVE_UPLOAD_EVENT_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Cached import promises — resolved once, reused thereafter. Cleared on
// failure so a later call can retry (e.g. a transient dynamic-import failure
// on a flaky connection).
let _uploaderImport = null;
let _filesystemImport = null;

function getUploader() {
  if (!_uploaderImport) {
    _uploaderImport = import("@capgo/capacitor-uploader")
      .then((mod) => mod.Uploader)
      .catch((error) => {
        _uploaderImport = null; // allow retry on next call
        throw error;
      });
  }
  return _uploaderImport;
}

function getFilesystem() {
  if (!_filesystemImport) {
    _filesystemImport = import("@capacitor/filesystem")
      .then((mod) => ({ Filesystem: mod.Filesystem, Directory: mod.Directory }))
      .catch((error) => {
        _filesystemImport = null; // allow retry on next call
        throw error;
      });
  }
  return _filesystemImport;
}

/**
 * Report a background-upload failure so it's visible in monitoring.
 * Reporting must never itself throw.
 */
function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_background_upload" },
    });
  } catch {
    // no-op
  }
}

/**
 * Whether the native background-upload plugin is available in the current
 * shell. False outside Capacitor, or if the native side never registered
 * the "Uploader" plugin (e.g. an app build predating the native shell work).
 * @returns {boolean}
 */
export function isBackgroundUploadAvailable() {
  return (
    isCapacitorEnvironment() && isCapacitorPluginAvailable(UPLOADER_PLUGIN_NAME)
  );
}

// Per-upload-id resolvers for in-flight `startUpload()` calls, fanned out to
// from the single `'events'` listener below.
const pendingUploads = new Map();

function acknowledge(Uploader, eventId) {
  // Progress ('uploading') events carry no eventId and need no ack. Completed
  // / failed events are cached natively for redelivery across app restarts —
  // ack removes them from that cache once handled, whether or not this JS
  // session still has a pending resolver for the id (e.g. after a cold
  // restart the old resolver is gone, but the stale cached event must still
  // be cleared or it re-fires forever on every future plugin init).
  if (!eventId) return;
  Uploader.acknowledgeEvent({ eventId }).catch(() => {
    // Best-effort — a failed ack just means the event may redeliver once more.
  });
}

function handleUploadEvent(Uploader, event) {
  const pending = pendingUploads.get(event.id);

  if (event.name === "uploading") {
    pending?.onProgress?.(event.payload?.percent ?? 0);
    return;
  }

  if (event.name === "completed") {
    pendingUploads.delete(event.id);
    const statusCode = event.payload?.statusCode;
    if (
      typeof statusCode === "number" &&
      statusCode >= 200 &&
      statusCode < 300
    ) {
      pending?.resolve({ statusCode });
    } else {
      const error = new Error(
        `Native background upload completed with non-2xx status ${statusCode}`,
      );
      error.statusCode = statusCode;
      pending?.reject(error);
    }
    acknowledge(Uploader, event.eventId);
    return;
  }

  if (event.name === "failed") {
    pendingUploads.delete(event.id);
    const error = new Error(
      event.payload?.error || "Native background upload failed",
    );
    const statusCode = event.payload?.statusCode;
    error.statusCode = statusCode;
    // A 'failed' event (as opposed to a non-2xx 'completed') means the
    // native side never got an HTTP response at all — connection lost,
    // DNS failure, cellular handoff, etc. There is no statusCode to classify
    // by, but this is exactly the transient-network class of failure the
    // caller's retry loop must retry (that's the whole reliability point of
    // this feature). Mark it explicitly so callers don't have to guess from
    // an absent statusCode.
    if (typeof statusCode !== "number") {
      error.nativeTransportFailure = true;
    }
    pending?.reject(error);
    acknowledge(Uploader, event.eventId);
  }
}

// Memoized 'events' listener registration promise. Awaited by
// nativeBackgroundPut (not fire-and-forget) so a registration failure
// rejects the caller's upload instead of leaving it hanging forever waiting
// for events that can never arrive.
let listenerPromise = null;

/**
 * Register the single 'events' listener, once. Must be called with an
 * already-resolved Uploader plugin reference (see getUploader()).
 * @returns {Promise<void>}
 */
function ensureEventListener(Uploader) {
  if (!listenerPromise) {
    listenerPromise = Uploader.addListener("events", (event) =>
      handleUploadEvent(Uploader, event),
    )
      .then(() => undefined)
      .catch((error) => {
        listenerPromise = null; // allow a later call to retry
        reportError(error);
        throw error;
      });
  }
  return listenerPromise;
}

/**
 * Start a native background PUT of a file already materialized on disk
 * (`fileUri`) to a presigned URL. Resolves once the native session reports
 * `completed` with a 2xx status; rejects on `failed`, any non-2xx
 * `completed`, or if no terminal event arrives within
 * NATIVE_UPLOAD_EVENT_TIMEOUT_MS. The upload continues even if the WebView is
 * torn down or backgrounded mid-transfer — that's the entire point of this
 * module.
 *
 * @param {object} args
 * @param {string} args.fileUri - `file://` path to the bytes to upload.
 * @param {string} args.url - presigned destination URL (e.g. S3 PUT URL).
 * @param {Object<string,string>} args.headers - headers to send verbatim.
 * @param {string} [args.mimeType] - content type of the file.
 * @param {(percent:number)=>void} [args.onProgress] - 0-100 progress callback.
 * @returns {Promise<{statusCode:number}>}
 */
export async function nativeBackgroundPut({
  fileUri,
  url,
  headers,
  mimeType,
  onProgress,
}) {
  const Uploader = await getUploader();
  // Propagates a registration failure as a rejection instead of silently
  // leaving the caller's promise (constructed below) waiting forever.
  await ensureEventListener(Uploader);

  const { id } = await Uploader.startUpload({
    filePath: fileUri,
    serverUrl: url,
    method: "PUT",
    headers,
    mimeType,
    uploadType: "binary",
    // Retry policy is fully owned by the JS caller (upload_video.js), which
    // re-presigns a fresh URL on every attempt. Letting the plugin ALSO
    // retry internally would stack attempts against a URL that only the
    // outer loop refreshes, so leave this at 0.
    maxRetries: 0,
    notificationTitle: "Uploading video…",
  });

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      pendingUploads.delete(id);
      const error = new Error(
        "Native background upload timed out waiting for a terminal event",
      );
      // Same shape as a 'failed' event with no statusCode (handleUploadEvent
      // above) — no HTTP response was ever received here either, just a
      // dropped/lost terminal event instead of a lost connection. Tag it
      // identically so upload_video.js's isRetriableNativeUploadError treats
      // it as the same transient, retriable class instead of a hard failure
      // (without this it has no statusCode, no axios .code/.response, and its
      // message doesn't contain the literal substring "timeout" that
      // isRetriableUploadError's string match looks for — "timed out" — so it
      // would otherwise fall through as non-retriable).
      error.nativeTransportFailure = true;
      reject(error);
    }, NATIVE_UPLOAD_EVENT_TIMEOUT_MS);

    const settle = (fn) => (value) => {
      clearTimeout(timeoutId);
      fn(value);
    };

    pendingUploads.set(id, {
      resolve: settle(resolve),
      reject: settle(reject),
      onProgress,
    });
  });
}

// Base64-encode an ArrayBuffer in fixed-size chunks (avoids blowing the
// String.fromCharCode.apply argument-count limit on large buffers).
const BASE64_CHUNK_BYTES = 0x8000; // 32KB
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += BASE64_CHUNK_BYTES) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(offset, offset + BASE64_CHUNK_BYTES),
    );
  }
  return btoa(binary);
}

// Read + base64-encode the file in bounded chunks rather than materializing
// one giant in-memory base64 string. A single-shot buffer -> binary-string ->
// base64-string pass on a 150MB file (this module's own size ceiling) would
// hold roughly 3x the file size in memory simultaneously (~500MB) — exactly
// the OOM risk NATIVE_UPLOAD_MAX_BYTES exists to bound. Chunking keeps peak
// memory to a small multiple of TEMP_FILE_CHUNK_BYTES regardless of file
// size. Mirrors the existing chunked-read pattern in upload_video.js's
// getFileMD5Base64.
const TEMP_FILE_CHUNK_BYTES = 4 * 1024 * 1024; // 4MB

/**
 * Materialize a picked File to a temp file on disk so the native background
 * session has a `file://` path to read from. Reads the file in bounded
 * chunks — same de-stale intent as `utils/prepare_image_for_upload.js`
 * (copying bytes into memory now makes the upload immune to the OS
 * invalidating the original File handle later, e.g. on backgrounding), but
 * chunked to bound peak memory. Caller is responsible for calling
 * `releaseNativeTempFile()` once the upload settles.
 *
 * @param {File} file
 * @returns {Promise<{uri:string, path:string}>}
 */
export async function materializeNativeTempFile(file) {
  const { Filesystem, Directory } = await getFilesystem();
  // Directory.LibraryNoCloud rather than Directory.Cache: Cache is explicitly
  // documented as OS-purgeable under disk pressure at any time, which would
  // undermine the exact reliability guarantee this feature exists to provide
  // (surviving a long-running, possibly backgrounded, transfer).
  // LibraryNoCloud persists for the app's lifetime and is excluded from
  // iCloud backup (this is transient upload scratch data, not user content
  // worth backing up).
  const directory = Directory.LibraryNoCloud;
  // Computed up front (not derived from anything the write loop produces) so
  // it's known even if a chunk write below throws before this function ever
  // returns — see the catch block's cleanup.
  const path = `tkc-native-uploads/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  try {
    let offset = 0;
    do {
      const end = Math.min(offset + TEMP_FILE_CHUNK_BYTES, file.size);
      // Sequential by design: each write must land before the next chunk is
      // read, bounding memory to one chunk in flight rather than the whole file.
      const chunk = file.slice(offset, end);
      const buffer = await chunk.arrayBuffer();
      const base64 = arrayBufferToBase64(buffer);
      if (offset === 0) {
        await Filesystem.writeFile({
          path,
          data: base64,
          directory,
          recursive: true,
        });
      } else {
        await Filesystem.appendFile({ path, data: base64, directory });
      }
      offset = end;
    } while (offset < file.size);
  } catch (error) {
    // A chunk write/append throwing here (e.g. disk full, OS killed the
    // write) means this function never returns `path` to the caller, so the
    // caller's `finally releaseNativeTempFile(tempFilePath)` never runs —
    // tempFilePath is only assigned from this function's successful return
    // value (see upload_video.js's uploadNativeBackground). Without this,
    // whatever bytes did land at `path` leak permanently in
    // Directory.LibraryNoCloud, which — unlike Cache — is NOT OS-purgeable.
    // Best-effort delete before re-throwing; deleteFile itself failing (e.g.
    // writeFile threw before creating anything) must not mask the original
    // error.
    try {
      await Filesystem.deleteFile({ path, directory });
    } catch {
      // No-op — nothing to clean up, or the delete lost a race. Either way
      // the original error is what the caller needs to see.
    }
    throw error;
  }

  const { uri } = await Filesystem.getUri({ path, directory });
  return { uri, path };
}

/**
 * Copy an already-on-disk native file (e.g. a screen recording's own
 * `fileUri`, written by the native plugin into the app's TEMP directory)
 * into the SAME persistent, non-purgeable location materializeNativeTempFile
 * writes camera/gallery picks to (Directory.LibraryNoCloud) — WITHOUT ever
 * reading its bytes into JS.
 *
 * WHY THIS EXISTS. A native background URLSession/WorkManager PUT reading
 * straight from the app's own temp directory does not reliably transfer,
 * which is the root cause of a screen recording's upload getting stuck at
 * 0% with no error. After this copy, the bytes handed to
 * nativeBackgroundPut sit in the exact same location, under the exact same
 * access, as the camera/gallery path this mirrors — the one already proven
 * to work. Unlike materializeNativeTempFile (whose source is a picked JS
 * File it must read and base64-encode), the source here is already a file
 * on native disk, so this copies file-to-file at the native layer instead.
 *
 * Caller is responsible for calling `releaseNativeTempFile()` on the
 * returned `path` once the upload settles, exactly as for
 * materializeNativeTempFile. The ORIGINAL file at `fileUri` is left
 * untouched — this only ever adds a copy.
 *
 * @param {string} fileUri - native `file://` path to copy FROM.
 * @returns {Promise<{uri:string, path:string}>}
 */
export async function copyNativeFileToUploads(fileUri) {
  const { Filesystem, Directory } = await getFilesystem();
  const directory = Directory.LibraryNoCloud;
  // Same path convention as materializeNativeTempFile: computed up front so
  // it's known even if the copy throws before this function returns.
  const path = `tkc-native-uploads/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2)}`;

  // Filesystem.copy does NOT create the destination's parent directory (on
  // iOS it's a bare FileManager.copyItem), unlike materializeNativeTempFile's
  // writeFile({ recursive: true }). On an install where no camera/gallery
  // upload has ever created tkc-native-uploads/, the copy below fails with
  // the generic OS-PLUG-FILE-0013. mkdir rejects when the directory already
  // exists (OS-PLUG-FILE-0010), which is the common case, so any mkdir error
  // is ignored — a genuine problem still surfaces from the copy itself.
  try {
    await Filesystem.mkdir({
      path: "tkc-native-uploads",
      directory,
      recursive: true,
    });
  } catch {
    // Already exists (expected), or a real failure the copy will report.
  }

  try {
    await Filesystem.copy({ from: fileUri, to: path, toDirectory: directory });
    const { uri } = await Filesystem.getUri({ path, directory });
    return { uri, path };
  } catch (error) {
    // Mirrors materializeNativeTempFile's own cleanup-on-throw: best-effort
    // delete whatever landed at `path` so a getUri failure after a
    // successful copy doesn't leak a permanent (non-OS-purgeable) file.
    try {
      await Filesystem.deleteFile({ path, directory });
    } catch {
      // No-op — nothing to clean up, or the delete lost a race. Either way
      // the original error is what the caller needs to see.
    }
    throw error;
  }
}

/**
 * Read a native `file://` path back as a JS `Blob`, the way the native shell
 * itself would: `Capacitor.convertFileSrc` (when present) to get a
 * webview-loadable URL, then `fetch` -> `blob()`. Bypasses convertFileSrc and
 * fetches the raw fileUri directly when it isn't available (e.g. outside the
 * Capacitor shell), so this also works in a plain browser given any
 * fetchable URI.
 *
 * Shared by every caller that needs to read bytes already materialized to
 * native disk (e.g. a compressed video written by video_compress.js) back
 * into memory: see upload_video.js's getCompressedFileMD5Base64, which used
 * to inline this exact fetch/blob pair, and readNativeFileUriAsFile below.
 *
 * @param {string} fileUri - native `file://` path (or any fetchable URI).
 * @returns {Promise<Blob>}
 */
export async function readNativeFileUriAsBlob(fileUri) {
  const src =
    typeof window !== "undefined" &&
    typeof window.Capacitor?.convertFileSrc === "function"
      ? window.Capacitor.convertFileSrc(fileUri)
      : fileUri;
  return await (await fetch(src)).blob();
}

/**
 * Read a native `file://` path back into a JS `File` (a named, typed Blob).
 * Built on readNativeFileUriAsBlob above.
 *
 * @param {string} fileUri - native `file://` path (or any fetchable URI).
 * @param {string} filename - name to give the resulting File.
 * @param {string} [mimeType] - content type of the resulting File.
 * @returns {Promise<File>}
 */
export async function readNativeFileUriAsFile(fileUri, filename, mimeType) {
  const blob = await readNativeFileUriAsBlob(fileUri);
  return new File([blob], filename, { type: mimeType });
}

// Decode a base64 string (as @capacitor/filesystem readFile returns binary
// data) into bytes.
function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Read a native `file://` path into a JS `File` THROUGH THE FILESYSTEM
 * PLUGIN (the native bridge), in bounded chunks.
 *
 * Unlike readNativeFileUriAsFile (a fetch of convertFileSrc(fileUri)), this
 * is not a network request, so it works on the remote-origin
 * (app.turnkey.coach) shell where that cross-origin fetch of a local file is
 * blocked. Each chunk is read with readFile's `offset`/`length` (plugin
 * 8.1.0+) so only one chunk's base64 string is in flight at a time. An older
 * native build that ignores offset/length returns the whole file on the
 * first read; that is detected (more bytes than asked for) and used as-is.
 *
 * @param {string} fileUri - native `file://` path to read.
 * @param {string} filename - name to give the resulting File.
 * @param {string} [mimeType] - content type of the resulting File.
 * @returns {Promise<File>}
 */
export async function readNativeFileViaFilesystem(fileUri, filename, mimeType) {
  const { Filesystem } = await getFilesystem();
  const { size } = await Filesystem.stat({ path: fileUri });

  const parts = [];
  let offset = 0;
  while (offset < size) {
    const length = Math.min(TEMP_FILE_CHUNK_BYTES, size - offset);
    const { data } = await Filesystem.readFile({
      path: fileUri,
      offset,
      length,
    });
    const bytes = base64ToBytes(data);
    if (offset === 0 && bytes.length >= size && length < size) {
      // offset/length ignored: this one read is the whole file.
      parts.push(bytes);
      break;
    }
    if (bytes.length === 0) {
      throw new Error(
        `Native file read returned no data at offset ${offset} of ${size}`,
      );
    }
    parts.push(bytes);
    offset += bytes.length;
  }

  return new File(parts, filename, { type: mimeType });
}

/**
 * Best-effort delete of a temp file written by materializeNativeTempFile().
 * Never throws — a leaked temp file is not itself dangerous and must never
 * mask or block the caller's upload outcome.
 * @param {string|null|undefined} path
 */
export async function releaseNativeTempFile(path) {
  if (!path) return;
  try {
    const { Filesystem, Directory } = await getFilesystem();
    await Filesystem.deleteFile({
      path,
      directory: Directory.LibraryNoCloud,
    });
  } catch (error) {
    reportError(error);
  }
}

export default {
  isBackgroundUploadAvailable,
  nativeBackgroundPut,
  materializeNativeTempFile,
  copyNativeFileToUploads,
  releaseNativeTempFile,
  readNativeFileUriAsBlob,
  readNativeFileUriAsFile,
  readNativeFileViaFilesystem,
  NATIVE_UPLOAD_MAX_BYTES,
};
